import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Check,
  X,
  Trash2,
  Music,
  Loader2,
  Eye,
  EyeOff,
  BarChart,
  ArrowUpDown,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { subsetsApi } from '../../api/subsets';
import { songsApi } from '../../api/songs';
import type { Subset, SubsetWithSongs, SongSummary } from '../../types';
import { SongSelectionModal } from './SongSelectionModal';
import { VoteStatsModal } from './VoteStatsModal';
import { SubsetOrderModal } from './SubsetOrderModal';
import { SongOrderModal } from './SongOrderModal';

export function SubsetManagement() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [subsets, setSubsets] = useState<Subset[]>([]);
  const [selectedSubset, setSelectedSubset] = useState<SubsetWithSongs | null>(null);
  const [songs, setSongs] = useState<SongSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [showSongSelector, setShowSongSelector] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsSubsetId, setStatsSubsetId] = useState<number | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showSongOrderModal, setShowSongOrderModal] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newSubsetName, setNewSubsetName] = useState('');

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsetsData, songsData] = await Promise.all([
        subsetsApi.getAllSubsets(),
        songsApi.getAllSongs(),
      ]);
      setSubsets(subsetsData);
      setSongs(songsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubsetDetails = async (id: number) => {
    try {
      const details = await subsetsApi.getSubsetById(id);
      setSelectedSubset(details);
    } catch (error) {
      console.error('Failed to load subset details:', error);
    }
  };

  const handleCreateSubset = async () => {
    if (!newSubsetName.trim()) return;

    try {
      await subsetsApi.createSubset(newSubsetName.trim());
      setNewSubsetName('');
      setCreatingNew(false);
      await loadData();
    } catch (error) {
      console.error('Failed to create subset:', error);
    }
  };

  const handleStartEdit = (subset: Subset) => {
    setEditingId(subset.id);
    setEditingName(subset.name);
  };

  const handleSaveEdit = async (id: number) => {
    if (!editingName.trim()) return;

    try {
      await subsetsApi.updateSubset(id, { name: editingName.trim() });
      setEditingId(null);
      await loadData();
      if (selectedSubset && selectedSubset.id === id) {
        await loadSubsetDetails(id);
      }
    } catch (error) {
      console.error('Failed to update subset:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleToggleVisibility = async (subset: Subset) => {
    try {
      await subsetsApi.updateSubset(subset.id, { adminOnly: !subset.adminOnly });
      await loadData();
      if (selectedSubset && selectedSubset.id === subset.id) {
        await loadSubsetDetails(subset.id);
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
    }
  };

  const handleDeleteSubset = async (id: number) => {
    if (!confirm('Are you sure you want to delete this subset?')) return;

    try {
      await subsetsApi.deleteSubset(id);
      if (selectedSubset && selectedSubset.id === id) {
        setSelectedSubset(null);
      }
      await loadData();
    } catch (error) {
      console.error('Failed to delete subset:', error);
    }
  };

  const handleSelectSubset = async (subset: Subset) => {
    await loadSubsetDetails(subset.id);
  };

  const handleSaveSelectedSongs = async (songIds: number[]) => {
    if (!selectedSubset) return;

    try {
      await subsetsApi.replaceSubsetSongs(selectedSubset.id, songIds);
      await loadSubsetDetails(selectedSubset.id);
      await loadData(); // Refresh to update song counts
      setShowSongSelector(false);
    } catch (error) {
      console.error('Failed to update songs:', error);
    }
  };

  const handleRemoveSong = async (songId: number) => {
    if (!selectedSubset) return;

    try {
      await subsetsApi.removeSongFromSubset(selectedSubset.id, songId);
      await loadSubsetDetails(selectedSubset.id);
      await loadData();
    } catch (error) {
      console.error('Failed to remove song:', error);
    }
  };

  const getSelectedSongs = (): SongSummary[] => {
    if (!selectedSubset) return [];
    return selectedSubset.songIds
      .map((id) => songs.find((s) => s.id === id))
      .filter((s): s is SongSummary => s !== undefined);
  };

  const handleSaveSongOrder = async (orderedSongIds: number[]) => {
    if (!selectedSubset) return;

    await subsetsApi.replaceSubsetSongs(selectedSubset.id, orderedSongIds);
    await loadSubsetDetails(selectedSubset.id);
    await loadData();
  };

  return (
    <div className="min-h-screen bg-monster-dark pb-8">
      {/* Header */}
      <header className="bg-monster-card border-b border-monster-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
            >
              <ArrowLeft size={28} />
            </button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-monster-blue to-monster-crimson bg-clip-text text-transparent">
              Subset Management
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Subset List */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Subsets</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowOrderModal(true)}
                  disabled={subsets.length < 2}
                  className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={subsets.length < 2 ? "Need at least 2 subsets to reorder" : "Reorder subsets"}
                >
                  <ArrowUpDown size={16} />
                  Reorder
                </button>
                <button
                  onClick={() => setCreatingNew(true)}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <Plus size={16} />
                  New Subset
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-monster-blue mx-auto" />
              </div>
            ) : (
              <div className="space-y-2">
                {/* New Subset Input */}
                {creatingNew && (
                  <div className="flex items-center gap-2 p-3 bg-monster-dark rounded">
                    <input
                      type="text"
                      value={newSubsetName}
                      onChange={(e) => setNewSubsetName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateSubset();
                        if (e.key === 'Escape') setCreatingNew(false);
                      }}
                      placeholder="Subset name..."
                      className="flex-1 bg-transparent border-none outline-none text-white"
                      autoFocus
                    />
                    <button
                      onClick={handleCreateSubset}
                      className="w-8 h-8 flex items-center justify-center rounded bg-green-500/10 hover:bg-green-500 text-green-500 hover:text-white transition-all flex-shrink-0"
                    >
                      <Check size={16} className="flex-shrink-0" />
                    </button>
                    <button
                      onClick={() => setCreatingNew(false)}
                      className="w-8 h-8 flex items-center justify-center rounded bg-monster-crimson/10 hover:bg-monster-crimson text-monster-crimson hover:text-white transition-all flex-shrink-0"
                    >
                      <X size={16} className="flex-shrink-0" />
                    </button>
                  </div>
                )}

                {/* Subset List */}
                {subsets.map((subset) => (
                  <div
                    key={subset.id}
                    className={`p-3 rounded transition-colors cursor-pointer ${
                      selectedSubset?.id === subset.id
                        ? 'bg-monster-blue/20 border border-monster-blue'
                        : 'bg-monster-dark hover:bg-monster-card/50'
                    }`}
                    onClick={() => handleSelectSubset(subset)}
                  >
                    <div className="flex items-center gap-2">
                      {/* Title */}
                      {editingId === subset.id ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(subset.id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          className="flex-1 bg-transparent border-none outline-none text-white font-medium"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      ) : (
                        <div className="flex-1">
                          <h3 className="font-semibold">{subset.name}</h3>
                          <p className="text-xs text-gray-400">
                            {subset.songCount || 0} song{subset.songCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {editingId === subset.id ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(subset.id)}
                              className="w-8 h-8 flex items-center justify-center rounded bg-green-500/10 hover:bg-green-500 text-green-500 hover:text-white transition-all flex-shrink-0"
                            >
                              <Check size={16} className="flex-shrink-0" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="w-8 h-8 flex items-center justify-center rounded bg-monster-crimson/10 hover:bg-monster-crimson text-monster-crimson hover:text-white transition-all flex-shrink-0"
                            >
                              <X size={16} className="flex-shrink-0" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleVisibility(subset);
                              }}
                              className={`w-8 h-8 flex items-center justify-center rounded transition-all flex-shrink-0 ${
                                subset.adminOnly
                                  ? 'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500 hover:text-white'
                                  : 'bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white'
                              }`}
                              title={subset.adminOnly ? 'Admin only - click to make visible to all' : 'Visible to all - click to make admin only'}
                            >
                              {subset.adminOnly ? <EyeOff size={16} className="flex-shrink-0" /> : <Eye size={16} className="flex-shrink-0" />}
                            </button>
                            <button
                              onClick={() => handleStartEdit(subset)}
                              className="w-8 h-8 flex items-center justify-center rounded bg-monster-blue/10 hover:bg-monster-blue text-monster-blue hover:text-white transition-all flex-shrink-0"
                            >
                              <Edit2 size={16} className="flex-shrink-0" />
                            </button>
                            <button
                              onClick={() => handleDeleteSubset(subset.id)}
                              className="w-8 h-8 flex items-center justify-center rounded bg-monster-crimson/10 hover:bg-monster-crimson text-monster-crimson hover:text-white transition-all flex-shrink-0"
                            >
                              <Trash2 size={16} className="flex-shrink-0" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {subsets.length === 0 && !creatingNew && (
                  <p className="text-center text-gray-400 py-8">
                    No subsets yet. Create one to get started!
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Right: Selected Subset Details */}
          <div className="card">
            <h2 className="text-2xl font-bold mb-4">
              {selectedSubset ? selectedSubset.name : 'Select a Subset'}
            </h2>

            {selectedSubset ? (
              <div className="space-y-4">
                {/* Visibility Status */}
                <div className="flex items-center gap-2 p-3 bg-monster-dark rounded">
                  {selectedSubset.adminOnly ? (
                    <>
                      <EyeOff className="text-yellow-500" size={20} />
                      <span className="text-sm text-yellow-500">Admin Only - Not visible to users</span>
                    </>
                  ) : (
                    <>
                      <Eye className="text-green-500" size={20} />
                      <span className="text-sm text-green-500">Visible to All Users</span>
                    </>
                  )}
                </div>

                {/* Vote Limit Input */}
                <div className="p-3 bg-monster-dark rounded">
                  <label className="block text-sm font-medium mb-2">
                    Vote Limit (songs per user)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={selectedSubset.songCount || 1}
                    value={selectedSubset.voteLimit}
                    onChange={async (e) => {
                      const newLimit = parseInt(e.target.value, 10);
                      if (newLimit > 0 && newLimit <= (selectedSubset.songCount || 1)) {
                        await subsetsApi.updateSubset(selectedSubset.id, { voteLimit: newLimit });
                        await loadSubsetDetails(selectedSubset.id);
                        await loadData();
                      }
                    }}
                    className="w-full px-3 py-2 bg-monster-card border border-monster-border rounded text-white focus:outline-none focus:border-monster-blue"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Users must select exactly {selectedSubset.voteLimit} song{selectedSubset.voteLimit !== 1 ? 's' : ''} to vote
                  </p>
                </div>

                {/* Download Toggle */}
                <div className="p-3 bg-monster-dark rounded">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="block text-sm font-medium mb-1">Allow Downloads</span>
                      <span className="text-xs text-gray-400">
                        Enable users to download all songs as a ZIP file
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        await subsetsApi.updateSubset(selectedSubset.id, {
                          downloadable: !selectedSubset.downloadable,
                        });
                        await loadSubsetDetails(selectedSubset.id);
                        await loadData();
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                        selectedSubset.downloadable ? 'bg-green-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                          selectedSubset.downloadable ? 'left-[22px]' : 'left-1'
                        }`}
                      />
                    </button>
                  </label>
                </div>

                {/* Voting Open Toggle */}
                <div className="p-3 bg-monster-dark rounded">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="block text-sm font-medium mb-1">Voting Open</span>
                      <span className="text-xs text-gray-400">
                        Allow users to submit and modify their votes
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        await subsetsApi.updateSubset(selectedSubset.id, {
                          votingOpen: !selectedSubset.votingOpen,
                        });
                        await loadSubsetDetails(selectedSubset.id);
                        await loadData();
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                        selectedSubset.votingOpen ? 'bg-green-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                          selectedSubset.votingOpen ? 'left-[22px]' : 'left-1'
                        }`}
                      />
                    </button>
                  </label>
                </div>

                {/* Show Live Tally Toggle */}
                <div className="p-3 bg-monster-dark rounded">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="block text-sm font-medium mb-1">Show Live Tally</span>
                      <span className="text-xs text-gray-400">
                        Display real-time vote results to all users
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        await subsetsApi.updateSubset(selectedSubset.id, {
                          showLiveTally: !selectedSubset.showLiveTally,
                        });
                        await loadSubsetDetails(selectedSubset.id);
                        await loadData();
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                        selectedSubset.showLiveTally ? 'bg-green-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                          selectedSubset.showLiveTally ? 'left-[22px]' : 'left-1'
                        }`}
                      />
                    </button>
                  </label>
                </div>

                {/* Special Categories Configuration */}
                <div className="p-4 bg-monster-dark rounded">
                  <h3 className="text-sm font-semibold mb-2">Special Voting Categories</h3>
                  <p className="text-xs text-gray-400 mb-4">
                    Enable optional category votes where users can pick one song per category (separate from main ranking).
                  </p>
                  <div className="space-y-3">
                    {selectedSubset.specialCategories.map((category, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <button
                          onClick={async () => {
                            const newCategories = [...selectedSubset.specialCategories];
                            newCategories[index] = {
                              ...category,
                              enabled: !category.enabled,
                            };
                            await subsetsApi.updateSubset(selectedSubset.id, {
                              specialCategories: newCategories,
                            });
                            await loadSubsetDetails(selectedSubset.id);
                            await loadData();
                          }}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            category.enabled ? 'bg-monster-blue' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`absolute h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                              category.enabled ? 'left-[22px]' : 'left-1'
                            }`}
                          />
                        </button>
                        <input
                          type="text"
                          value={category.name}
                          onChange={async (e) => {
                            const newCategories = [...selectedSubset.specialCategories];
                            newCategories[index] = {
                              ...category,
                              name: e.target.value,
                            };
                            await subsetsApi.updateSubset(selectedSubset.id, {
                              specialCategories: newCategories,
                            });
                            await loadSubsetDetails(selectedSubset.id);
                            await loadData();
                          }}
                          disabled={!category.enabled}
                          className={`flex-1 px-3 py-2 bg-monster-card border border-monster-border rounded text-sm focus:outline-none focus:border-monster-blue ${
                            !category.enabled ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          placeholder={`Category ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Enabled categories will appear in the voting modal for users to select.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setShowSongSelector(true)}
                    className="btn-primary flex items-center justify-center gap-2"
                  >
                    <Music size={20} />
                    Select Songs ({selectedSubset.songIds.length})
                  </button>
                  <button
                    onClick={() => setShowSongOrderModal(true)}
                    disabled={selectedSubset.songIds.length < 2}
                    className="btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={selectedSubset.songIds.length < 2 ? "Need at least 2 songs to reorder" : "Reorder songs"}
                  >
                    <ArrowUpDown size={20} />
                    Reorder Songs
                  </button>
                  <button
                    onClick={() => {
                      setStatsSubsetId(selectedSubset.id);
                      setShowStatsModal(true);
                    }}
                    className="btn-primary flex items-center justify-center gap-2 col-span-2"
                  >
                    <BarChart size={20} />
                    View Stats
                  </button>
                </div>

                {/* Songs List */}
                <div>
                  <h3 className="font-semibold mb-2">Songs in this subset:</h3>
                  {getSelectedSongs().length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {getSelectedSongs().map((song) => (
                        <div
                          key={song.id}
                          className="flex items-center gap-3 p-2 bg-monster-dark rounded group"
                        >
                          <img
                            src={song.imagePath}
                            alt={song.title}
                            className="w-12 h-12 rounded object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{song.title}</p>
                            <p className="text-xs text-gray-400 truncate">{song.artist}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveSong(song.id)}
                            className="w-8 h-8 flex items-center justify-center rounded bg-monster-crimson/10 hover:bg-monster-crimson text-monster-crimson hover:text-white transition-all flex-shrink-0"
                          >
                            <Trash2 size={16} className="flex-shrink-0" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-400 py-8">
                      No songs selected. Click "Select Songs" to add some!
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-400 py-12">
                Select a subset from the list to manage its songs.
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Song Selection Modal */}
      {showSongSelector && selectedSubset && (
        <SongSelectionModal
          songs={songs}
          selectedSongIds={selectedSubset.songIds}
          onSave={handleSaveSelectedSongs}
          onClose={() => setShowSongSelector(false)}
        />
      )}

      {/* Vote Stats Modal */}
      {showStatsModal && statsSubsetId !== null && (
        <VoteStatsModal
          subsetId={statsSubsetId}
          onClose={() => {
            setShowStatsModal(false);
            setStatsSubsetId(null);
          }}
        />
      )}

      {/* Subset Order Modal */}
      {showOrderModal && (
        <SubsetOrderModal
          subsets={subsets}
          onClose={() => setShowOrderModal(false)}
          onSave={loadData}
        />
      )}

      {/* Song Order Modal */}
      {showSongOrderModal && selectedSubset && (
        <SongOrderModal
          songs={getSelectedSongs()}
          subsetName={selectedSubset.name}
          onSave={handleSaveSongOrder}
          onClose={() => setShowSongOrderModal(false)}
        />
      )}
    </div>
  );
}
